import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { ArrowLeft, BookOpen, Sparkles } from 'lucide-react';

export default function NVCComponents() {
  const navigate = useNavigate();

  const components = [
    {
      number: 1,
      title: 'Observations',
      page: 64,
      icon: '👁️',
      color: 'from-blue-900/30 to-cyan-900/30',
      borderColor: 'border-blue-700/30',
      description: 'Observations are what we see or hear that we identify as the stimulus to our reactions.',
      keyPoint: 'Video Camera Clarity',
      details: [
        'Describe what we are reacting to concretely, specifically and neutrally',
        'Much as a video camera might capture the moment',
        'Creates shared reality with the other person',
        'Separate judgments, evaluations or interpretations from description',
      ],
      example: {
        judgment: '"You\'re rude"',
        observation: '"When you walked in you did not say hello to me"'
      }
    },
    {
      number: 2,
      title: 'Feelings',
      page: 65,
      icon: '❤️',
      color: 'from-rose-900/30 to-pink-900/30',
      borderColor: 'border-rose-700/30',
      description: 'Feelings represent our emotional experience and physical sensations associated with our needs.',
      keyPoint: 'Inner Experience, Not Interpretation',
      details: [
        'Identify, name and connect with feelings',
        'Focus on words that describe our inner experience',
        'Not interpretations of people\'s actions',
        'Taking responsibility for our experience',
      ],
      example: {
        judgment: '"I feel like you don\'t love me"',
        observation: '"I feel lonely"'
      }
    },
    {
      number: 3,
      title: 'Needs',
      page: 66,
      icon: '💎',
      color: 'from-purple-900/30 to-violet-900/30',
      borderColor: 'border-purple-700/30',
      description: 'Our needs are an expression of our deepest shared humanity.',
      keyPoint: 'Universal Human Experience',
      details: [
        'Core values and deepest human longings',
        'What is most alive in us',
        'Shared by all human beings',
        'Not specific strategies, people, places, or actions',
      ],
      example: {
        judgment: '"I want you to come to my birthday party"',
        observation: '"I have needs for love and connection"'
      }
    },
    {
      number: 4,
      title: 'Requests',
      page: 67,
      icon: '🤝',
      color: 'from-green-900/30 to-emerald-900/30',
      borderColor: 'border-green-700/30',
      description: 'We make requests to assess cooperation for strategies to meet our needs.',
      keyPoint: 'Concrete, Doable, True Choice',
      details: [
        'Identify specific action that will serve our needs',
        'Check willingness to participate',
        'Connection requests vs. solution requests',
        'Willingness to hear "no" and continue dialogue',
      ],
      example: {
        judgment: '"I would like you to always come on time"',
        observation: '"Would you be willing to spend 15 minutes talking about what may help you arrive at 9 am?"'
      }
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <div className="border-b border-slate-700/50 bg-slate-900/90 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate('/belonging-ebook')}
                variant="outline"
                size="sm"
                className="border-slate-600 hover:border-slate-500 text-white hover:bg-slate-800"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Guide
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-white">The Components of Nonviolent Communication</h1>
              </div>
            </div>
            <div className="flex items-center gap-2 text-slate-400 text-sm">
              <BookOpen className="h-4 w-4" />
              <span>Page 64</span>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 py-12">
        <div className="max-w-6xl mx-auto">
          {/* Introduction */}
          <div className="text-center mb-12">
            <Sparkles className="h-20 w-20 text-purple-400 mx-auto mb-6" />
            <h2 className="text-4xl font-bold text-white mb-6">The Four Components of NVC</h2>
            <p className="text-xl text-slate-300 leading-relaxed max-w-4xl mx-auto mb-6">
              The practice also includes empathic connection with ourselves—"self empathy." The purpose of self-empathy 
              is to support us in maintaining connection with our own needs, thus encouraging us to choose our actions 
              and responses based on self-connection and self-acceptance.
            </p>
            <p className="text-lg text-slate-400 leading-relaxed max-w-4xl mx-auto">
              NVC was developed by Dr. Marshall B. Rosenberg, who has introduced it to individuals and organizations 
              world-wide. It has been used between warring tribes and in war-torn countries; in schools, prisons, 
              and corporations; in health care, social change, and government institutions; and in intimate personal relationships.
            </p>
          </div>

          {/* Four Components */}
          <div className="space-y-8 mb-12">
            {components.map((component, idx) => (
              <div
                key={idx}
                className={`bg-gradient-to-br ${component.color} border ${component.borderColor} rounded-lg overflow-hidden hover:scale-[1.01] transition-transform`}
                data-testid={`component-${idx}`}
              >
                {/* Header */}
                <div className="bg-slate-900/40 px-6 py-4 border-b border-white/10">
                  <div className="flex items-center gap-4">
                    <div className="text-4xl">{component.icon}</div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <span className="text-white/60 font-mono text-lg">{component.number}</span>
                        <h3 className="text-3xl font-bold text-white">{component.title}</h3>
                      </div>
                      <p className="text-slate-300 italic mt-1">{component.keyPoint}</p>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <p className="text-slate-100 text-lg leading-relaxed mb-6">
                    {component.description}
                  </p>

                  {/* Key Details */}
                  <div className="bg-slate-900/40 rounded-lg p-6 mb-6">
                    <h4 className="text-white font-semibold mb-4">Key Points:</h4>
                    <ul className="space-y-2">
                      {component.details.map((detail, detailIdx) => (
                        <li key={detailIdx} className="flex items-start gap-3">
                          <span className="text-white/60 mt-1">•</span>
                          <span className="text-slate-200">{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Example */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-red-900/30 border border-red-700/30 rounded p-4">
                      <p className="text-red-200 text-sm font-semibold mb-2">❌ Less Effective:</p>
                      <p className="text-red-100">{component.example.judgment}</p>
                    </div>
                    <div className="bg-green-900/30 border border-green-700/30 rounded p-4">
                      <p className="text-green-200 text-sm font-semibold mb-2">✅ More Effective:</p>
                      <p className="text-green-100">{component.example.observation}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Summary */}
          <div className="bg-gradient-to-br from-amber-900/20 to-orange-900/20 border border-amber-700/30 rounded-lg p-8">
            <h3 className="text-2xl font-bold text-white mb-6">Summary of NVC Principles</h3>
            <div className="space-y-3 text-slate-200">
              <p className="flex items-start gap-3">
                <span className="text-blue-400">1.</span>
                <span>State concrete actions you observe in yourself or the other person</span>
              </p>
              <p className="flex items-start gap-3">
                <span className="text-rose-400">2.</span>
                <span>State the feeling that the observation is triggering in you. Or, guess what the other person is feeling</span>
              </p>
              <p className="flex items-start gap-3">
                <span className="text-purple-400">3.</span>
                <span>State the need that is the cause of that feeling. Or, guess the need that caused the feeling in the other person</span>
              </p>
              <p className="flex items-start gap-3">
                <span className="text-green-400">4.</span>
                <span>Make a concrete request for action to meet the need just identified</span>
              </p>
            </div>
            <p className="text-slate-400 text-sm mt-6 italic text-center">
              Source: http://www.wikihow.com/Practice-Nonviolent-Communication
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
